import React, { useEffect, useRef } from "react";

const LINK_DISTANCE = 132;
// The mockup draws 55 nodes and up to 7 pulses on a ~1090x493 canvas. The real
// hero is much larger, so node count and pulses scale with area to keep the
// same density instead of thinning the network out.
const NODE_DENSITY = 55 / (1090 * 493);
const MIN_NODES = 30;
const MAX_NODES = 220;
const PULSES_PER_NODE = 7 / 55;
const MAX_SPOKES = 4;
const SATELLITE_COUNT = 7;
// Below md the hub sits under the hero copy, so only the ambient network runs.
const MOBILE_QUERY = "(max-width: 767.98px)";

const initNetwork = (canvas) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return () => {};

    const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
    ).matches;

    let width = 0;
    let height = 0;
    let nodes = [];
    let pulses = [];
    let hub = null;
    let satellites = [];
    let spokes = [];
    let tick = 0;
    let maxPulses = 7;
    let frameId = null;
    let isIntersecting = false;
    let showSystem = true;

    const placeSatellites = () => {
        satellites.forEach((satellite) => {
            satellite.x = hub.x + Math.cos(satellite.angle) * satellite.radius;
            satellite.y =
                hub.y + Math.sin(satellite.angle) * satellite.radius * 0.78;
        });
    };

    const build = () => {
        const rect = canvas.getBoundingClientRect();
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        width = Math.max(rect.width, 1);
        height = Math.max(rect.height, 1);
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const nodeCount = Math.round(
            Math.min(
                MAX_NODES,
                Math.max(MIN_NODES, width * height * NODE_DENSITY)
            )
        );
        maxPulses = Math.max(7, Math.round(nodeCount * PULSES_PER_NODE));
        nodes = Array.from({ length: nodeCount }, () => ({
            x: Math.random() * width,
            y: Math.random() * height,
            vx: (Math.random() - 0.5) * 0.17,
            vy: (Math.random() - 0.5) * 0.17,
            r: 1 + Math.random() * 1.5,
        }));
        pulses = [];
        showSystem = !window.matchMedia(MOBILE_QUERY).matches;

        // The standing system in the right half: one hub, orbiting satellites.
        const systemSize = Math.min(height * 0.3, width * 0.135);
        hub = { x: width * 0.765, y: height * 0.56, r: 3.6 };
        satellites = Array.from({ length: SATELLITE_COUNT }, (_, index) => {
            const radius =
                systemSize *
                (0.4 + 0.6 * (index / (SATELLITE_COUNT - 1))) *
                (0.92 + Math.random() * 0.16);

            return {
                angle: Math.random() * Math.PI * 2,
                radius,
                speed:
                    (0.0016 / Math.pow(radius / systemSize, 0.55)) *
                    (0.75 + Math.random() * 0.5),
                r: 1.6 + Math.random() * 1.3,
                x: 0,
                y: 0,
            };
        });
        spokes = [];
        if (!showSystem) satellites = [];
        placeSatellites();
    };

    const line = (a, b, color) => {
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
    };

    const dot = (x, y, r, color) => {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
    };

    const glowingDot = (x, y, r, color, shadowColor, shadowBlur) => {
        ctx.save();
        ctx.shadowColor = shadowColor;
        ctx.shadowBlur = shadowBlur;
        dot(x, y, r, color);
        ctx.restore();
    };

    const distanceWithinLink = (a, b) => {
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const squared = dx * dx + dy * dy;

        return squared > LINK_DISTANCE * LINK_DISTANCE
            ? null
            : Math.sqrt(squared);
    };

    const moveNodes = () => {
        nodes.forEach((node) => {
            node.x += node.vx;
            node.y += node.vy;
            if (node.x < -24) node.x = width + 24;
            else if (node.x > width + 24) node.x = -24;
            if (node.y < -24) node.y = height + 24;
            else if (node.y > height + 24) node.y = -24;
        });
    };

    const moveSystem = () => {
        tick += 1;
        if (!showSystem) return;
        satellites.forEach((satellite) => {
            satellite.angle += satellite.speed;
        });
        placeSatellites();

        if (spokes.length < MAX_SPOKES && Math.random() < 0.05) {
            spokes.push({
                satellite: Math.floor(Math.random() * satellites.length),
                t: 0,
                outbound: Math.random() < 0.5,
            });
        }
    };

    const drawLinks = (move) => {
        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                const distance = distanceWithinLink(nodes[i], nodes[j]);
                if (distance === null) continue;

                const alpha = 0.16 * (1 - distance / LINK_DISTANCE);
                line(nodes[i], nodes[j], `rgba(140, 178, 228, ${alpha})`);

                if (
                    move &&
                    pulses.length < maxPulses &&
                    Math.random() < 0.0016
                ) {
                    pulses.push({ a: nodes[i], b: nodes[j], t: 0 });
                }
            }
        }
    };

    const drawSystem = () => {
        // Ambient nodes that come close are drawn into the system.
        nodes.forEach((node) => {
            satellites.forEach((satellite) => {
                const distance = distanceWithinLink(node, satellite);
                if (distance === null) return;

                const alpha = 0.14 * (1 - distance / LINK_DISTANCE);
                line(node, satellite, `rgba(140, 178, 228, ${alpha})`);
            });
        });

        satellites.forEach((satellite, index) => {
            const next = satellites[(index + 1) % satellites.length];
            line(satellite, next, "rgba(150, 190, 240, 0.13)");
        });

        satellites.forEach((satellite) => {
            line(hub, satellite, "rgba(158, 198, 245, 0.30)");
        });

        nodes.forEach((node) => {
            dot(node.x, node.y, node.r, "rgba(158, 192, 234, 0.34)");
        });

        satellites.forEach((satellite) => {
            dot(
                satellite.x,
                satellite.y,
                satellite.r,
                "rgba(184, 212, 248, 0.68)"
            );
        });

        if (!showSystem) return;

        // Hub: soft coral ring, bright core.
        const breathe = 0.5 + 0.5 * Math.sin(tick * 0.012);
        ctx.strokeStyle = `rgba(226, 62, 82, ${0.16 + 0.16 * breathe})`;
        ctx.beginPath();
        ctx.arc(hub.x, hub.y, 11 + 2.5 * breathe, 0, Math.PI * 2);
        ctx.stroke();
        glowingDot(
            hub.x,
            hub.y,
            hub.r,
            "rgba(226, 238, 255, 0.92)",
            "rgba(150, 195, 245, 0.8)",
            12
        );
    };

    // Signals running in and out of the hub.
    const drawSpokes = (move) => {
        spokes = spokes.filter((spoke) => {
            if (move) spoke.t += 0.011;
            if (spoke.t >= 1) return false;

            const satellite = satellites[spoke.satellite];
            const t = spoke.outbound ? spoke.t : 1 - spoke.t;
            const fade = Math.sin(Math.PI * spoke.t);

            glowingDot(
                hub.x + (satellite.x - hub.x) * t,
                hub.y + (satellite.y - hub.y) * t,
                2.4,
                `rgba(226, 62, 82, ${0.95 * fade})`,
                "rgba(226, 62, 82, 0.9)",
                10
            );
            return true;
        });
    };

    const drawPulses = (move) => {
        pulses = pulses.filter((pulse) => {
            if (move) pulse.t += 0.013;
            if (pulse.t >= 1) return false;

            const fade = Math.sin(Math.PI * pulse.t);

            glowingDot(
                pulse.a.x + (pulse.b.x - pulse.a.x) * pulse.t,
                pulse.a.y + (pulse.b.y - pulse.a.y) * pulse.t,
                2.1,
                `rgba(226, 62, 82, ${0.9 * fade})`,
                "rgba(226, 62, 82, 0.85)",
                9
            );
            return true;
        });
    };

    const draw = (move) => {
        ctx.clearRect(0, 0, width, height);
        ctx.lineWidth = 1;

        if (move) moveNodes();
        drawLinks(move);
        if (move) moveSystem();
        drawSystem();
        drawSpokes(move);
        drawPulses(move);
    };

    const frame = () => {
        draw(true);
        frameId = window.requestAnimationFrame(frame);
    };

    const stop = () => {
        if (frameId === null) return;
        window.cancelAnimationFrame(frameId);
        frameId = null;
    };

    const syncPlayback = () => {
        if (reduceMotion) return;

        if (isIntersecting && !document.hidden) {
            if (frameId === null) {
                frameId = window.requestAnimationFrame(frame);
            }
        } else {
            stop();
        }
    };

    const rebuild = () => {
        build();
        draw(false);
    };

    const resizeObserver = new ResizeObserver(rebuild);
    const intersectionObserver = new IntersectionObserver(
        ([entry]) => {
            isIntersecting = entry.isIntersecting;
            syncPlayback();
        },
        { threshold: 0.05 }
    );

    rebuild();
    resizeObserver.observe(canvas);
    intersectionObserver.observe(canvas);
    document.addEventListener("visibilitychange", syncPlayback);

    return () => {
        stop();
        resizeObserver.disconnect();
        intersectionObserver.disconnect();
        document.removeEventListener("visibilitychange", syncPlayback);
    };
};

const HeroAnimation = () => {
    const canvasRef = useRef(null);

    useEffect(() => initNetwork(canvasRef.current), []);

    return (
        <div className="hero-animation" aria-hidden="true">
            <div className="hero-animation__glow" />
            <canvas ref={canvasRef} className="hero-animation__canvas" />
        </div>
    );
};

export default HeroAnimation;
